import "@/index.css";
import "@/App.css";
import axios from "axios";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { TenantProvider, useTenant } from "./contexts/TenantContext";
import Layout from "./components/Layout";
import InactivityTimer from "./components/InactivityTimer";
import { PwaInstallPrompt } from "./components/PwaInstallPrompt";
import { Guard503A } from "./components/Guard503A";

import SystemBanner from "./components/SystemBanner";

// Enable httpOnly cookie-based auth: browser sends cookies automatically with every request
axios.defaults.withCredentials = true;

// CSRF protection: store token from response headers (works cross-origin)
// and attach as X-CSRF-Token on every mutating request
axios.interceptors.response.use((response) => {
    const newCsrf = response.headers['x-csrf-token'];
    if (newCsrf) {
        sessionStorage.setItem('labflo_csrf_token', newCsrf);
    }
    return response;
});

axios.interceptors.request.use((config) => {
    const method = (config.method || '').toUpperCase();
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
        // Prefer document.cookie — it's ALWAYS the freshest server-truth
        // (updated by every Set-Cookie response). sessionStorage is a
        // fallback for scenarios where document.cookie isn't readable
        // (cross-origin, sandboxed iframes, etc.).
        let token: string | undefined;
        const csrfCookie = document.cookie
            .split('; ')
            .find((c) => c.startsWith('labflo_csrf='));
        if (csrfCookie) {
            token = decodeURIComponent(csrfCookie.split('=')[1]);
        }
        if (!token) {
            token = sessionStorage.getItem('labflo_csrf_token') || undefined;
        }
        if (token) {
            config.headers['X-CSRF-Token'] = token;
        }
    }
    return config;
});

const LoginPage = lazy(() => import("./pages/LoginPage"));
const Login503BPage = lazy(() => import("./pages/Login503BPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const TrainingPage = lazy(() => import("./pages/TrainingPage"));
const Compliance503BPage = lazy(() => import("./pages/Compliance503BPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const TimecardPage = lazy(() => import("./pages/TimecardPage"));
const TrainingPopoutPage = lazy(() => import("./pages/TrainingPopoutPage"));
const FormulasPage = lazy(() => import("./pages/FormulasPage"));
const FormulaDetailPage = lazy(() => import("./pages/FormulaDetailPage"));
const CompoundingLogsPage = lazy(() => import("./pages/CompoundingLogsPage"));
const CompoundingLogDetailPage = lazy(() => import("./pages/CompoundingLogDetailPage"));
const CompoundingDrugDetailPage = lazy(() => import("./pages/CompoundingDrugDetailPage"));
const InventoryPage = lazy(() => import("./pages/InventoryRouter"));
const Orders503BPage = lazy(() => import("./pages/Orders503BPage"));
const CustomerPortalPage = lazy(() => import("./pages/CustomerPortalPage"));
const VendorPortalPage = lazy(() => import("./pages/VendorPortalPage"));
const InvoiceHistoryPage = lazy(() => import("./pages/InvoiceHistoryPage"));
const PayrollExportPage = lazy(() => import("./pages/PayrollExportPage"));
const TimeclockAppPage = lazy(() => import("./pages/TimeclockAppPage"));
const SuperAdminPage = lazy(() => import("./pages/SuperAdminPage"));
const ComplianceBriefingPrintPage = lazy(() => import("./pages/ComplianceBriefingPrintPage"));
const DirectoryPage = lazy(() => import("./pages/DirectoryPage"));
const MarketingLandingPage = lazy(() => import("./pages/MarketingLandingPage"));
const PortalPharmacyAccessPage = lazy(() => import("./pages/PortalPharmacyAccessPage"));
const Portal503BAccessPage = lazy(() => import("./pages/Portal503BAccessPage"));
const PortalClinicAccessPage = lazy(() => import("./pages/PortalClinicAccessPage"));
const PharmacyTenantQuickRoute = lazy(() => import("./pages/PharmacyTenantQuickRoute"));
const ClinicTenantQuickRoute = lazy(() => import("./pages/ClinicTenantQuickRoute"));
const PharmacyMarketingDemoPage = lazy(() => import("./pages/PharmacyMarketingDemoPage"));
const ClinicMarketingDemoPage = lazy(() => import("./pages/ClinicMarketingDemoPage"));
const SubscriptionGatePage = lazy(() => import("./pages/SubscriptionGatePage"));
const EmrPharmacyPage = lazy(() => import("./pages/EmrPharmacyPage"));
const SopPage = lazy(() => import("./pages/SopPage"));
const ComplianceCenterPage = lazy(() => import("./pages/ComplianceCenterPage"));
const ClinicLoginPage = lazy(() => import("./pages/ClinicLoginPage"));
const ClinicPortalPage = lazy(() => import("./pages/ClinicPortalPage"));
const ChatPopoutPage = lazy(() => import("./pages/ChatPopoutPage"));
const WallboardPicker = lazy(() => import("./pages/WallboardPage").then(m => ({ default: m.WallboardPicker })));
const WallboardScreen = lazy(() => import("./pages/WallboardPage").then(m => ({ default: m.WallboardScreen })));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const SystemRequirementsPage = lazy(() => import("./pages/SystemRequirementsPage"));
const SingleVsMultiPackSyringesPage = lazy(() => import("./pages/blog/SingleVsMultiPackSyringesPage"));
const ReportBuilderPage = lazy(() => import("./pages/ReportBuilderPage"));
const ITSupportLoginPage = lazy(() => import("./pages/ITSupportLoginPage"));

// Prefetch high-traffic routes after initial load
const prefetchRoutes = () => {
    requestIdleCallback(() => {
        import("./pages/DashboardPage");
        import("./pages/LoginPage");
        import("./pages/ClinicLoginPage");
        import("./pages/EmrPharmacyPage");
        import("./pages/AdminPage");
    });
};

const AppLoader = () => (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#040012' }}>
        <div className="w-8 h-8 spinner border-violet-500" />
    </div>
);

const ProtectedRoute = ({ children, reportsOnly = false }: { children: React.ReactNode; reportsOnly?: boolean }) => {
    const { user, loading } = useAuth();
    const { isSubscriptionActive, loading: tenantLoading } = useTenant();

    if (loading || tenantLoading) {
        return <AppLoader />;
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // If subscription expired and this is NOT a reports-only route, show gate
    if (!isSubscriptionActive && !reportsOnly) {
        return <SubscriptionGatePage />;
    }

    return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
    const { user, loading } = useAuth();
    
    if (loading) {
        return <AppLoader />;
    }
    
    if (user) {
        return <Navigate to="/dashboard" replace />;
    }
    
    return <>{children}</>;
};

function AppRoutes() {
    const { isPlatformSubdomain } = useTenant();

    useEffect(() => {
        prefetchRoutes();
    }, []);

    // platform.labflo.org → super admin portal
    if (isPlatformSubdomain) {
        return (
            <Suspense fallback={<AppLoader />}>
                <Routes>
                    <Route path="/super-admin/compliance-briefing" element={<ComplianceBriefingPrintPage />} />
                    <Route path="*" element={<SuperAdminPage />} />
                </Routes>
            </Suspense>
        );
    }

    return (
        <Suspense fallback={<AppLoader />}>
            <Routes>
            {/* Directory / landing page — shown at root with no tenant */}
            <Route path="/" element={<MarketingLandingPage />} />
            <Route path="/directory" element={<DirectoryPage />} />
            <Route path="/portal/pharmacy" element={<PortalPharmacyAccessPage />} />
            <Route path="/portal/503b" element={<Portal503BAccessPage />} />
            <Route path="/portal/clinic" element={<PortalClinicAccessPage />} />
            {/* Pretty-URL tenant quick routes — bypass portal step using cached access. */}
            <Route path="/p/:slug" element={<PharmacyTenantQuickRoute />} />
            <Route path="/c/:slug" element={<ClinicTenantQuickRoute />} />
            <Route path="/demo/pharmacy" element={<PharmacyMarketingDemoPage />} />
            <Route path="/demo/clinic" element={<ClinicMarketingDemoPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/about" element={<Navigate to="/" replace />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/system-requirements" element={<SystemRequirementsPage />} />
            <Route path="/customer-portal" element={<CustomerPortalPage />} />
            <Route path="/vendor-portal" element={<VendorPortalPage />} />
            <Route path="/blog/single-vs-multipack-syringes" element={<SingleVsMultiPackSyringesPage />} />
            {/* Explicit login route for tenant */}
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                }
            />
            <Route
                path="/login-503b"
                element={
                    <PublicRoute>
                        <Login503BPage />
                    </PublicRoute>
                }
            />
            <Route path="/clinic/login" element={<ClinicLoginPage />} />
            <Route path="/clinic/portal" element={<ClinicPortalPage />} />
            <Route path="/chat-popout" element={<ChatPopoutPage />} />
            <Route path="/wallboard" element={<ProtectedRoute><WallboardPicker /></ProtectedRoute>} />
            <Route path="/wallboard/screen" element={<ProtectedRoute><WallboardScreen /></ProtectedRoute>} />
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <Guard503A><DashboardPage /></Guard503A>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports" 
                element={
                    <ProtectedRoute reportsOnly>
                        <Guard503A><ReportsPage /></Guard503A>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin" 
                element={
                    <ProtectedRoute>
                        <AdminPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/training" 
                element={
                    <ProtectedRoute>
                        <Guard503A><TrainingPage /></Guard503A>
                    </ProtectedRoute>
                } 
            />
            <Route
                path="/503b"
                element={
                    <ProtectedRoute>
                        <Compliance503BPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/503b/:moduleKey"
                element={
                    <ProtectedRoute>
                        <Compliance503BPage />
                    </ProtectedRoute>
                }
            />
            <Route 
                path="/profile" 
                element={
                    <ProtectedRoute>
                        <ProfilePage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/audit" 
                element={<Navigate to="/reports" replace />}
            />
            <Route 
                path="/timecard" 
                element={
                    <ProtectedRoute>
                        <TimecardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/emr"
                element={
                    <ProtectedRoute>
                        <Guard503A><EmrPharmacyPage /></Guard503A>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/sops"
                element={
                    <ProtectedRoute>
                        <SopPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/compliance-center"
                element={
                    <ProtectedRoute>
                        <ComplianceCenterPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/report-builder"
                element={
                    <ProtectedRoute>
                        <ReportBuilderPage />
                    </ProtectedRoute>
                }
            />
            <Route 
                path="/formulas" 
                element={
                    <ProtectedRoute>
                        <FormulasPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/formulas/:id" 
                element={
                    <ProtectedRoute>
                        <FormulaDetailPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/compounding" 
                element={
                    <ProtectedRoute>
                        <Guard503A><CompoundingLogsPage /></Guard503A>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/compounding/drug/:formulaId" 
                element={
                    <ProtectedRoute>
                        <CompoundingDrugDetailPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/compounding/:id" 
                element={
                    <ProtectedRoute>
                        <CompoundingLogDetailPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/inventory" 
                element={
                    <ProtectedRoute>
                        <Guard503A><InventoryPage /></Guard503A>
                    </ProtectedRoute>
                } 
            />
            <Route
                path="/orders"
                element={
                    <ProtectedRoute>
                        <Orders503BPage />
                    </ProtectedRoute>
                }
            />
            <Route 
                path="/invoices" 
                element={
                    <ProtectedRoute>
                        <InvoiceHistoryPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/payroll-export" 
                element={
                    <ProtectedRoute>
                        <PayrollExportPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/training-popout" 
                element={
                    <TrainingPopoutPage />
                } 
            />
            <Route path="/rocketship" element={<SuperAdminPage />} />
            <Route path="/super-admin/compliance-briefing" element={<ComplianceBriefingPrintPage />} />
            <Route path="/itsupportlogin" element={<ITSupportLoginPage />} />
            <Route path="/timeclock-app" element={<TimeclockAppPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
    );
}

function App() {
    return (
        <ThemeProvider>
            <BrowserRouter>
                <TenantProvider>
                    <AuthProvider>
                        <SystemBanner />
                        <InactivityTimer />
                            <AppRoutes />
                        <Toaster position="top-right" richColors closeButton />
                        <PwaInstallPrompt />
                    </AuthProvider>
                </TenantProvider>
            </BrowserRouter>
        </ThemeProvider>
    );
}

export default App;
